import React, { useState, useEffect, useContext } from 'react';
import { AiOutlineClose, AiOutlineHeart } from 'react-icons/ai';
import { Link } from 'react-router-dom';
import { FaShoppingCart, FaCheck, FaTrash } from 'react-icons/fa';
import { getWishlist, removeFromWishlist, fetchWishlist } from '../../utils/wishlistSync';
import { fetchCartItems } from '../../utils/cartSync';
import { AuthContext } from '../../context/AuthContext';
import https from '../../utils/https';
import { toast } from '../../utils/toast';

interface WishlistModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenCart?: () => void;
}

const WishlistSkeleton = () => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Array(3).fill(0).map((_, i) => (
                <div key={i} className="wishlist-skeleton-row">
                    <div className="wishlist-skeleton-img shimmer-effect" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="wishlist-skeleton-line shimmer-effect" style={{ width: '80%', height: '16px' }} />
                        <div className="wishlist-skeleton-line shimmer-effect" style={{ width: '40%', height: '14px' }} />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                            <div className="wishlist-skeleton-btn shimmer-effect" style={{ flex: 1, height: '36px', borderRadius: '8px' }} />
                            <div className="wishlist-skeleton-btn shimmer-effect" style={{ width: '36px', height: '36px', borderRadius: '8px' }} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const WishlistModal = ({ isOpen, onClose, onOpenCart }: WishlistModalProps) => {
    const { isAuth } = useContext(AuthContext);
    const [wishlist, setWishlist] = useState<any[]>(isAuth ? getWishlist() : []);
    const [addedProducts, setAddedProducts] = useState<{ [key: string]: boolean }>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isAuth) {
            setWishlist([]);
            return;
        }

        const updateWishlist = () => {
            setWishlist(getWishlist());
        };

        window.addEventListener('wishlistUpdated', updateWishlist);
        if (isOpen) {
            setLoading(true);
            fetchWishlist().finally(() => {
                setLoading(false);
            });
        }

        return () => {
            window.removeEventListener('wishlistUpdated', updateWishlist);
        };
    }, [isOpen, isAuth]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleAddToCart = async (product: any) => {
        try {
            const cleanId = product._id || product.id;
            if (!cleanId) return;

            const parsedPrice = (() => {
                const p = product.price;
                if (!p) return 0;
                if (Array.isArray(p)) {
                    const match = p.find(
                        (item: any) => item && (item.type === "single" || item.type === "coil")
                    );
                    if (match && typeof match.price === "number") return match.price;
                    const firstPriceObj = p[0];
                    if (firstPriceObj && typeof firstPriceObj.price === "number") {
                        return firstPriceObj.price;
                    }
                    return 0;
                }
                if (typeof p === "number") return p;
                return parseFloat(String(p).replace(/[^\d.]/g, "")) || 0;
            })();

            const parsedOriginalPrice = (() => {
                const originalPrice = product.mrp || product.original_price || product.originalPrice;
                if (originalPrice) {
                    if (Array.isArray(originalPrice)) {
                        const match = originalPrice.find(
                            (item: any) => item && (item.type === "single" || item.type === "coil")
                        );
                        if (match && typeof match.price === "number") return match.price;
                        const firstPriceObj = originalPrice[0];
                        if (firstPriceObj && typeof firstPriceObj.price === "number") {
                            return firstPriceObj.price;
                        }
                        return 0;
                    }
                    if (typeof originalPrice === "number") return originalPrice;
                    return parseFloat(String(originalPrice).replace(/[^\d.]/g, "")) || 0;
                }
                if (Array.isArray(product.price)) {
                    const match = product.price.find(
                        (item: any) => item && (item.type === "single" || item.type === "coil")
                    );
                    if (match && typeof match.mrp === "number") return match.mrp;
                    const firstPriceObj = product.price[0];
                    if (firstPriceObj && typeof firstPriceObj.mrp === "number") {
                        return firstPriceObj.mrp;
                    }
                }
                return Math.ceil(parsedPrice * 1.15);
            })();

            const fallbackImage = "/images/products/placeholder.png";
            const imageUrl = product.image || (product.images && product.images.length > 0 ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url) : '') || fallbackImage;

            const cartItem = {
                id: cleanId,
                name: product.name,
                image: imageUrl,
                ram: "Standard",
                subtotal: parsedPrice,
                cuttoff: `₹${Math.ceil(parsedOriginalPrice).toLocaleString()}`,
                no: 1,
                priceA: parsedPrice
            };

            if (isAuth) {
                await https.post('cart/add', { product_id: cleanId, quantity: 1 });
                await fetchCartItems();
            } else {
                const currentCartStr = localStorage.getItem('honeywill_cart');
                const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];

                const existingIndex = cartList.findIndex(item => item.id === cartItem.id);
                if (existingIndex > -1) {
                    cartList[existingIndex].no = 1;
                    cartList[existingIndex].subtotal = cartList[existingIndex].priceA;
                } else {
                    cartList.push(cartItem);
                }

                localStorage.setItem('honeywill_cart', JSON.stringify(cartList));
            }

            window.dispatchEvent(new Event('cartUpdated'));

            toast.success("Added to Cart successfully!");

            // Show visual added state
            setAddedProducts(prev => ({ ...prev, [cleanId]: true }));
            setTimeout(() => {
                setAddedProducts(prev => ({ ...prev, [cleanId]: false }));
            }, 2000);
        } catch (error) {
            console.error("Error adding to cart in WishlistModal:", error);
            toast.error("Failed to add product to cart");
        }
    };

    const filteredProducts = wishlist.filter((p: any) =>
        p && !(p.name || '').toLowerCase().includes('soft copper pipe pro 1')
    );

    return (
        <div className="wishlist-modal-overlay" onClick={onClose}>
            <style>
                {`
                .wishlist-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    justify-content: flex-end;
                    background-color: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    animation: fadeInOverlay 0.3s ease;
                }
                
                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                
                .wishlist-drawer {
                    animation: slideInRight 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                    width: 32% !important;
                    min-width: 440px !important;
                    background-color: #ffffff;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: -15px 0 45px rgba(15, 23, 42, 0.08);
                    border-top-left-radius: 24px;
                    border-bottom-left-radius: 24px;
                    overflow: hidden;
                }
                
                .wishlist-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px;
                    border-bottom: 1px solid #f3f4f6;
                    background-color: #ffffff;
                }

                .wishlist-title-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .wishlist-title {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 800;
                    color: #1f2937;
                    letter-spacing: -0.5px;
                }

                .wishlist-count-badge {
                    background: #e6f9fa;
                    color: #14958f;
                    font-size: 11.5px;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 20px;
                    letter-spacing: 0.3px;
                }
                
                .wishlist-close-btn {
                    background: #f3f4f6;
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .wishlist-close-btn:hover {
                    background-color: #e5e7eb;
                    color: #111827;
                    transform: scale(1.05);
                }

                .wishlist-search-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .wishlist-search-icon {
                    position: absolute;
                    left: 14px;
                    color: #9ca3af;
                    font-size: 16px;
                }

                .wishlist-search-input {
                    width: 100%;
                    padding: 11px 14px 11px 40px;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    font-size: 13.5px;
                    outline: none;
                    transition: all 0.2s ease;
                    background-color: #f9fafb;
                    color: #1f2937;
                }

                .wishlist-search-input:focus {
                    border-color: #43c8ca;
                    background-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(67, 200, 202, 0.15);
                }

                .wishlist-search-clear {
                    position: absolute;
                    right: 12px;
                    background: #e5e7eb;
                    border: none;
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: background 0.2s;
                }

                .wishlist-search-clear:hover {
                    background: #d1d5db;
                }
                
                .wishlist-content {
                    padding: 24px;
                    overflow-y: auto;
                    flex: 1;
                    background-color: #ffffff;
                    display: flex;
                    flex-direction: column;
                }

                .wishlist-list {
                    display: flex;
                    flex-direction: column;
                }

                .wishlist-item-row {
                    display: flex;
                    gap: 16px;
                    padding: 16px 0;
                    border-bottom: 1px solid #f3f4f6;
                    align-items: flex-start;
                }

                .wishlist-item-row:last-child {
                    border-bottom: none;
                }

                .wishlist-img-box {
                    width: 80px;
                    height: 80px;
                    flex-shrink: 0;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fafafa;
                }

                .wishlist-img-box img {
                    max-width: 90%;
                    max-height: 90%;
                    object-fit: contain;
                }

                .wishlist-details-box {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    text-align: left;
                }

                .wishlist-item-title {
                    font-size: 14.5px;
                    font-weight: 600;
                    color: #1f2937;
                    line-height: 1.4;
                    margin: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .wishlist-price-block {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                }

                .wishlist-price-current {
                    font-weight: 800;
                    color: #1f2937;
                    font-size: 16px;
                }

                .wishlist-price-mrp {
                    text-decoration: line-through;
                    color: #9ca3af;
                    font-size: 13.5px;
                }

                .wishlist-price-discount {
                    color: #16a34a;
                    font-weight: 700;
                    font-size: 13.5px;
                }

                .wishlist-actions-row {
                    display: flex;
                    gap: 8px;
                    width: 100%;
                }

                .wishlist-add-to-cart-btn {
                    flex: 1;
                    background-color: #ffffff;
                    color: #1f2937;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    padding: 8px 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }

                .wishlist-add-to-cart-btn:hover {
                    background-color: #f9fafb;
                    border-color: #9ca3af;
                }

                .wishlist-add-to-cart-btn.added {
                    background-color: #10b981;
                    color: #ffffff;
                    border-color: #10b981;
                }

                .wishlist-remove-btn {
                    width: 36px;
                    height: 36px;
                    background-color: #ffffff;
                    color: #ef4444;
                    border: 1px solid #fee2e2;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }

                .wishlist-remove-btn:hover {
                    background-color: #fef2f2;
                    border-color: #fca5a5;
                }

                .wishlist-empty {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    flex: 1;
                    text-align: center;
                    padding: 40px 24px;
                }

                .wishlist-empty-icon-wrapper {
                    width: 90px;
                    height: 90px;
                    background-color: #fff1f2;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 24px;
                    box-shadow: 0 8px 20px rgba(255, 71, 87, 0.06);
                }

                .wishlist-empty-icon {
                    font-size: 40px;
                    color: #ff4757;
                }

                .wishlist-empty h3 {
                    font-size: 19px;
                    font-weight: 800;
                    color: #1f2937;
                    margin: 0 0 8px 0;
                    letter-spacing: -0.3px;
                }

                .wishlist-empty p {
                    font-size: 13.5px;
                    color: #6b7280;
                    margin: 0 0 24px 0;
                    max-width: 280px;
                    line-height: 1.5;
                }

                .wishlist-empty-cta {
                    padding: 12px 28px;
                    background-color: #43c8ca;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    text-decoration: none;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.2);
                }

                .wishlist-empty-cta:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(67, 200, 202, 0.3);
                }

                .wishlist-bag-btn {
                    width: 100%;
                    padding: 14px;
                    background-color: #43c8ca;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    box-shadow: 0 4px 14px rgba(67, 200, 202, 0.2);
                }

                .wishlist-bag-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(67, 200, 202, 0.3);
                }

                /* Skeletons */
                .wishlist-skeleton-row {
                    display: flex;
                    gap: 16px;
                    padding: 16px 0;
                    border-bottom: 1px solid #f3f4f6;
                    align-items: flex-start;
                }

                .wishlist-skeleton-img {
                    width: 80px;
                    height: 80px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    flex-shrink: 0;
                }

                .wishlist-skeleton-line {
                    height: 12px;
                    background: #f3f4f6;
                    border-radius: 6px;
                }

                .wishlist-skeleton-btn {
                    height: 36px;
                    background: #f3f4f6;
                }

                .shimmer-effect {
                    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
                    background-size: 200% 100%;
                    animation: shimmer-anim 1.5s infinite;
                }

                @keyframes shimmer-anim {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }

                @media (max-width: 768px) {
                    .wishlist-drawer {
                        width: 100% !important;
                        min-width: unset !important;
                        border-radius: 0;
                    }
                    .wishlist-header {
                        padding: 16px 20px !important;
                    }
                    .wishlist-content {
                        padding: 16px !important;
                    }
                }
                `}
            </style>

            <div className="wishlist-drawer" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="wishlist-header">
                    <div className="wishlist-title-container">
                        <h2 className="wishlist-title">My Wishlist</h2>
                        {isAuth && (
                            <span className="wishlist-count-badge">
                                {wishlist.length} {wishlist.length === 1 ? 'item' : 'items'}
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="wishlist-close-btn" aria-label="Close wishlist">
                        <AiOutlineClose size={18} />
                    </button>
                </div>


                {/* Content */}
                <div className="wishlist-content">
                    {!isAuth ? (
                        <div className="wishlist-empty">
                            <div className="wishlist-empty-icon-wrapper">
                                <AiOutlineHeart className="wishlist-empty-icon" />
                            </div>
                            <h3>Login to View Wishlist</h3>
                            <p>You need to be logged in to view items in your wishlist.</p>
                            <Link to="/login" onClick={onClose} className="wishlist-empty-cta" style={{ backgroundColor: '#43c8ca' }}>
                                Login
                            </Link>
                        </div>
                    ) : loading ? (
                        <WishlistSkeleton />
                    ) : filteredProducts.length > 0 ? (
                        <div className="wishlist-list">
                            {filteredProducts.map((product: any) => {
                                const imageUrl = product.image || (product.images && product.images.length > 0 ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url) : '') || "/images/products/placeholder.png";
                                const resolvedPrice = (() => {
                                    const p = product.price;
                                    if (!p) return 0;
                                    if (Array.isArray(p)) {
                                        const match = p.find(
                                            (item: any) => item && (item.type === "single" || item.type === "coil")
                                        );
                                        if (match && typeof match.price === "number") return match.price;
                                        const firstPriceObj = p[0];
                                        if (firstPriceObj && typeof firstPriceObj.price === "number") {
                                            return firstPriceObj.price;
                                        }
                                        return 0;
                                    }
                                    if (typeof p === "number") return p;
                                    return parseFloat(String(p).replace(/[^\d.]/g, "")) || 0;
                                })();

                                const resolvedMrp = (() => {
                                    const originalPrice = product.mrp || product.original_price || product.originalPrice;
                                    if (originalPrice) {
                                        if (Array.isArray(originalPrice)) {
                                            const match = originalPrice.find(
                                                (item: any) => item && (item.type === "single" || item.type === "coil")
                                            );
                                            if (match && typeof match.price === "number") return match.price;
                                            const firstPriceObj = originalPrice[0];
                                            if (firstPriceObj && typeof firstPriceObj.price === "number") {
                                                return firstPriceObj.price;
                                            }
                                            return 0;
                                        }
                                        if (typeof originalPrice === "number") return originalPrice;
                                        return parseFloat(String(originalPrice).replace(/[^\d.]/g, "")) || 0;
                                    }
                                    // Fallback: Check if price array has mrp
                                    if (Array.isArray(product.price)) {
                                        const match = product.price.find(
                                            (item: any) => item && (item.type === "single" || item.type === "coil")
                                        );
                                        if (match && typeof match.mrp === "number") return match.mrp;
                                        const firstPriceObj = product.price[0];
                                        if (firstPriceObj && typeof firstPriceObj.mrp === "number") {
                                            return firstPriceObj.mrp;
                                        }
                                    }
                                    return Math.ceil(resolvedPrice * 1.5);
                                })();

                                const discountPercent = resolvedMrp > resolvedPrice ? Math.round(((resolvedMrp - resolvedPrice) / resolvedMrp) * 100) : 0;

                                return (
                                    <div key={product._id} className="wishlist-item-row">
                                        {/* Image */}
                                        <Link to={`/product/${product._id}`} onClick={onClose} className="wishlist-img-box" style={{ textDecoration: 'none' }}>
                                            <img
                                                src={imageUrl}
                                                alt={product.name}
                                                onError={(e) => { (e.target as HTMLImageElement).src = '/images/products/placeholder.png'; }}
                                            />
                                        </Link>

                                        {/* Details & Action buttons */}
                                        <div className="wishlist-details-box">
                                            <Link to={`/product/${product._id}`} onClick={onClose} style={{ textDecoration: 'none' }}>
                                                <h4 className="wishlist-item-title">{product.name}</h4>
                                            </Link>
                                            
                                            <div className="wishlist-price-block">
                                                <span className="wishlist-price-current">₹{resolvedPrice.toLocaleString('en-IN')}</span>
                                                {resolvedMrp > resolvedPrice && (
                                                    <>
                                                        <span className="wishlist-price-mrp">₹{resolvedMrp.toLocaleString('en-IN')}</span>
                                                        <span className="wishlist-price-discount">{discountPercent}% Off</span>
                                                    </>
                                                )}
                                            </div>

                                            <div className="wishlist-actions-row">
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleAddToCart(product);
                                                    }}
                                                    className={`wishlist-add-to-cart-btn ${addedProducts[product._id] ? 'added' : ''}`}
                                                >
                                                    {addedProducts[product._id] ? (
                                                        <><FaCheck size="11px" /> ADDED</>
                                                    ) : (
                                                        <><FaShoppingCart size="11px" /> Add to Cart</>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        removeFromWishlist(product._id);
                                                    }}
                                                    className="wishlist-remove-btn"
                                                    title="Remove from Wishlist"
                                                >
                                                    <FaTrash size="14px" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="wishlist-empty">
                            <div className="wishlist-empty-icon-wrapper">
                                <AiOutlineHeart className="wishlist-empty-icon" />
                            </div>
                            <h3>Your Wishlist is Empty</h3>
                            <p>Explore products and add them to your wishlist</p>
                            <button onClick={onClose} className="wishlist-empty-cta" style={{ backgroundColor: '#43c8ca', border: 'none' }}>
                                Start Shopping
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {isAuth && wishlist.length > 0 && onOpenCart && (
                    <div style={{ padding: '20px 24px', borderTop: '1px solid #f3f4f6', backgroundColor: '#ffffff', boxShadow: '0 -10px 30px rgba(0,0,0,0.02)' }}>
                        <button
                            onClick={onOpenCart}
                            className="wishlist-bag-btn"
                        >
                            Go To Bag
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WishlistModal;
